import React from 'react';
import { getCropTypeInfo } from '../utils/croplandCrosAPI';

const MapLegend = ({ activeLayer }) => {
  // Helper function to render descriptions for each layer type
  const getLayerDescription = (layer) => {
    const descriptions = {
      crops: 'Shows the distribution of different crop types across agricultural lands in the DMV region. Colors represent different crop varieties.',
      ndvi: 'Normalized Difference Vegetation Index measures vegetation health and density. Higher values (green) indicate healthier vegetation.',
      evi: 'Enhanced Vegetation Index is optimized for high biomass regions. It reduces atmospheric influences and soil background noise.',
      thermal: 'Surface temperature measurements from thermal imagery. Helps identify water stress and irrigation needs.',
      ndre: 'Normalized Difference Red Edge is sensitive to chlorophyll content and is useful for detecting early signs of plant stress.'
    };
    
    return descriptions[layer] || '';
  };

  const renderLegendItems = () => {
    switch (activeLayer) {
      case 'crops':
        // Get common DMV crops for the legend
        const commonCrops = ['Corn', 'Soybeans', 'Wheat', 'Alfalfa', 'Cotton'];
        return (
          <>
            {commonCrops.map(crop => {
              const cropInfo = getCropTypeInfo(crop);
              return (
                <div className="legend-item" key={crop}>
                  <div className="legend-color" style={{ backgroundColor: cropInfo?.color || '#808080' }}></div>
                  <div className="legend-label">{crop}</div>
                </div>
              );
            })}
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#808080' }}></div>
              <div className="legend-label">Other</div>
            </div>
          </>
        );
      case 'ndvi':
        return (
          <>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#d73027' }}></div>
              <div className="legend-label">0.0 - 0.2 (Very Low Vegetation)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#fc8d59' }}></div>
              <div className="legend-label">0.2 - 0.4 (Low Vegetation)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#fee08b' }}></div>
              <div className="legend-label">0.4 - 0.6 (Moderate Vegetation)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#d9ef8b' }}></div>
              <div className="legend-label">0.6 - 0.8 (Dense Vegetation)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#91cf60' }}></div>
              <div className="legend-label">0.8 - 1.0 (Very Dense, Healthy Vegetation)</div>
            </div>
          </>
        );
      case 'evi':
        return (
          <>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#a50026' }}></div>
              <div className="legend-label">0.0 - 0.2 (Bare Soil/Stressed Vegetation)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#d73027' }}></div>
              <div className="legend-label">0.2 - 0.4 (Sparse Vegetation)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#f46d43' }}></div>
              <div className="legend-label">0.4 - 0.6 (Moderate Canopy)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#74add1' }}></div>
              <div className="legend-label">0.6 - 0.8 (Dense Canopy)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#4575b4' }}></div>
              <div className="legend-label">0.8 - 1.0 (Very Dense, Healthy Canopy)</div>
            </div>
          </>
        );
      case 'thermal':
        return (
          <>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#313695' }}></div>
              <div className="legend-label">10°C - 15°C (Cool - Well Irrigated)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#4575b4' }}></div>
              <div className="legend-label">15°C - 20°C (Mild - Adequate Moisture)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#74add1' }}></div>
              <div className="legend-label">20°C - 25°C (Moderate - Normal Conditions)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#fdae61' }}></div>
              <div className="legend-label">25°C - 30°C (Warm - Slight Stress)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#f46d43' }}></div>
              <div className="legend-label">30°C - 35°C (Hot - Moderate Stress)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#d73027' }}></div>
              <div className="legend-label">35°C - 40°C (Very Hot - High Stress)</div>
            </div>
          </>
        );
      case 'ndre':
        return (
          <>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#8e0152' }}></div>
              <div className="legend-label">0.0 - 0.2 (Very Low Chlorophyll)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#c51b7d' }}></div>
              <div className="legend-label">0.2 - 0.4 (Low Chlorophyll)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#de77ae' }}></div>
              <div className="legend-label">0.4 - 0.6 (Moderate Chlorophyll)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#7fbc41' }}></div>
              <div className="legend-label">0.6 - 0.8 (High Chlorophyll)</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#4d9221' }}></div>
              <div className="legend-label">0.8 - 1.0 (Very High Chlorophyll)</div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  if (!activeLayer || !['ndvi', 'evi', 'thermal', 'ndre', 'crops'].includes(activeLayer)) {
    return null;
  }

  const legendTitles = {
    ndvi: 'NDVI (Normalized Difference Vegetation Index)',
    evi: 'EVI (Enhanced Vegetation Index)',
    thermal: 'Thermal Imagery (°C)',
    ndre: 'NDRE (Normalized Difference Red Edge)',
    crops: 'Crop Types in DMV Region'
  };

  return (
    <div className="map-legend">
      <div className="legend-title">{legendTitles[activeLayer]}</div>
      <div className="legend-description">{getLayerDescription(activeLayer)}</div>
      {renderLegendItems()}
    </div>
  );
};

export default MapLegend; 